import { listen, setAttribute, unify } from "@lernetz/svelte-common/actions";
import type { Readable } from "svelte/store";

export type FieldNode = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export function createField<Values>(
  node: FieldNode,
  name: keyof Values,
  store: Readable<Values>,
  setValue: (name: keyof Values, value: unknown) => void,
  getValue: (name: keyof Values) => unknown,
  isMultiNode: (name: keyof Values) => boolean,
) {
  if (node instanceof HTMLInputElement && node.type == "checkbox") {
  }

  const bind = () => {
    // set the name attribute to the node
    setAttribute(node, { name });

    // initialize the node with the current value
    handleValueChange();

    return unify([
      // listen to node changes to update store
      listen(node, {
        type:
          node instanceof HTMLInputElement && (node.type === "checkbox" || node.type === "radio")
            ? "change"
            : "input",
        handler: handleDomChange,
      }),

      // listen to store changes to update node
      {
        destroy: store.subscribe(handleValueChange),
      },
    ]);
  };

  // if the node changes, update the store
  const handleDomChange = () => {
    // handle fields that have more than one node
    if (isMultiNode(name)) {
      // handle multiple checkboxes they are stored as arrays
      if (node instanceof HTMLInputElement && node.type === "checkbox") {
        let current = getValue(name) as Array<string>;
        if (!Array.isArray(current)) current = [];
        setValue(
          name,
          node.checked ? [...current, node.value] : current.filter((i) => i != node.value),
        );
      }

      // handle multiple radio inputs, they are stored as a string
      if (node instanceof HTMLInputElement && node.type === "radio") {
        setValue(name, node.value);
      }

      return;
    }

    // single checkbox will turn into boolean value
    if (node instanceof HTMLInputElement && node.type === "checkbox") {
      setValue(name, node.checked);
    }

    // multi select with turn into string array
    else if (node instanceof HTMLSelectElement && node.multiple) {
      setValue(
        name,
        Array.from(node.selectedOptions).map((o) => o.value),
      );
    }

    // regular select with turn into string value
    else if (node instanceof HTMLSelectElement) {
      setValue(name, node.value);
    }

    // bind all regular inputs as string
    else if (node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement) {
      setValue(name, node.value);
    }
  };

  // if the store changes, update the node
  const handleValueChange = () => {
    const value = getValue(name);

    // handle multiple checkboxes where value is stored as array
    if (node instanceof HTMLInputElement && node.type === "checkbox" && Array.isArray(value)) {
      node.checked = value.includes(node.value);
    }

    // handle radio input where value is stored as string
    else if (
      node instanceof HTMLInputElement &&
      node.type === "radio" &&
      typeof value === "string"
    ) {
      node.checked = value == node.value;
    }

    // handle checkboxes where value is stored as boolean
    else if (
      node instanceof HTMLInputElement &&
      node.type === "checkbox" &&
      typeof value === "boolean"
    ) {
      node.checked = value;
    }

    // multi select with turn into string array
    else if (node instanceof HTMLSelectElement && node.multiple && Array.isArray(value)) {
      for (const option of node.options) {
        option.selected = value.includes(option.value);
      }
    }

    // all other simply set the value
    else {
      node.value = value as string;
    }
  };

  return {
    name,
    bind,
  };
}
